import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getAccountCapabilities } from '../../utils/mindmap/apiClient';
import MapsMenu from './MapsMenu';
import UpgradeDialog from './UpgradeDialog';

const TopBar = ({
  isVisible,
  onSignInClick,
  onMouseLeaveBar,
  user,
  baseNodeTitle,
  currentMap,
  mapFileActions,
  onSignOut,
}) => {
  const hideTimeoutRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    if (!user) {
      setAccount(null);
      return undefined;
    }
    let cancelled = false;
    getAccountCapabilities()
      .then((result) => {
        if (!cancelled) setAccount(result);
      })
      .catch(() => {
        // Plan details remain available on demand through the dialog.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const handleCapabilitiesChanged = (event) => {
      if (event.detail) setAccount(event.detail);
    };
    window.addEventListener('mindmappi:accountCapabilitiesChanged', handleCapabilitiesChanged);
    return () => {
      window.removeEventListener('mindmappi:accountCapabilitiesChanged', handleCapabilitiesChanged);
    };
  }, []);

  const isDarkMode = useMemo(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('mindMapTheme');
      if (stored === 'light') return false;
      if (stored === 'dark') return true;
    } catch {
      // ignore
    }
    return true;
  }, []);

  const barStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    backgroundColor: isDarkMode
      ? 'rgba(45, 45, 45, 0.7)'
      : 'rgba(255, 255, 255, 0.7)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    zIndex: 200,
    color: isDarkMode ? '#f9fafb' : '#111827',
    transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
    transition: 'transform 300ms ease-out',
  };

  const logoStyle = {
    border: 'none',
    padding: 0,
    background: 'transparent',
    color: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontWeight: 800,
    letterSpacing: '0.04em',
    fontSize: 20,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    cursor: 'pointer',
  };
  const betaStyle = {
    marginLeft: 1,
    alignSelf: 'flex-start',
    fontSize: 10,
    lineHeight: 1,
    fontStyle: 'italic',
    fontWeight: 600,
    letterSpacing: '0.02em',
    opacity: 0.78,
  };
  const helpMapId = process.env.REACT_APP_HELP_MAP_ID || 'HELP';
  const handleLogoClick = () => {
    if (typeof window === 'undefined') return;
    window.location.assign('/');
  };
  const handleHelpClick = () => {
    if (typeof window === 'undefined') return;
    const url = `/?map=${encodeURIComponent(helpMapId)}`;
    window.location.assign(url);
  };
  const handleBugReportClick = () => {
    if (typeof window === 'undefined') return;
    window.location.href = 'mailto:gavin@mindmappi.com?subject=Mindmappi%20beta%20bug%20report';
  };

  const contextTitleStyle = {
    marginLeft: 8,
    fontSize: 13,
    opacity: 0.9,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: 260,
  };

  const signInButtonStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 13,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: isDarkMode ? '#1f2933' : 'white',
    color: isDarkMode ? '#f9fafb' : '#111827',
    cursor: 'pointer',
  };

  const signedInLabelStyle = {
    border: 'none',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 13,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    background: isDarkMode ? 'rgba(17, 24, 39, 0.85)' : 'rgba(243, 244, 246, 0.9)',
    color: isDarkMode ? '#f9fafb' : '#111827',
    cursor: 'pointer',
  };

  const avatarStyle = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: isDarkMode ? '#111827' : '#e5e7eb',
    color: isDarkMode ? '#f9fafb' : '#111827',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  const menuStyle = {
    position: 'absolute',
    top: 40,
    right: 0,
    minWidth: 140,
    padding: 8,
    borderRadius: 8,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
    border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    zIndex: 210,
  };

  const menuItemStyle = {
    fontSize: 13,
    padding: '6px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: isDarkMode ? '#e5e7eb' : '#111827',
  };

  const initials = useMemo(() => {
    if (!user || !user.displayName) return '';
    const parts = user.displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] || '') : '';
    const letters = `${first}${last}`.toUpperCase();
    return letters || '';
  }, [user]);

  return (
    <div
      style={barStyle}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      }}
      onMouseLeave={() => {
        if (!onMouseLeaveBar) return;
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
          onMouseLeaveBar();
          hideTimeoutRef.current = null;
        }, 500);
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <button type="button" style={logoStyle} onClick={handleLogoClick} aria-label="Open my Mindmappi map">
          <img
            src="/mindmappi-mark-transparent.png"
            width="28"
            height="28"
            alt=""
            aria-hidden="true"
            style={{ display: 'block', objectFit: 'contain' }}
          />
          <span>mindmappi</span>
          <span style={betaStyle}>beta</span>
        </button>
        {baseNodeTitle && (
          <div style={contextTitleStyle}>
            {'> '}{baseNodeTitle}
          </div>
        )}
      </div>
      {user ? (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: isDarkMode ? '#111827' : '#e5e7eb',
              color: isDarkMode ? '#f9fafb' : '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              fontWeight: 700,
              fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleHelpClick();
            }}
            title="Help"
          >
            ?
          </div>
          <MapsMenu
            currentMap={currentMap}
            isDarkMode={isDarkMode}
            mapFileActions={mapFileActions}
            onOpen={() => setMenuOpen(false)}
          />
          <button
            type="button"
            aria-label="User menu"
            aria-expanded={menuOpen}
            style={signedInLabelStyle}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((open) => !open);
            }}
          >
            <div style={avatarStyle}>
              {initials || '✓'}
            </div>
          </button>
          {menuOpen && (
            <div
              style={menuStyle}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <button
                type="button"
                style={{
                  ...menuItemStyle,
                  width: '100%',
                  border: 0,
                  background: 'transparent',
                  textAlign: 'left',
                  fontWeight: 700,
                }}
                onClick={() => {
                  setPlanDialogOpen(true);
                  setMenuOpen(false);
                }}
              >
                Plan: {account?.plan?.label || 'View plans'}
              </button>
              <div
                style={menuItemStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => {
                  handleBugReportClick();
                  setMenuOpen(false);
                }}
              >
                Report a bug
              </div>
              <div
                style={menuItemStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => {
                  if (onSignOut) onSignOut();
                  setMenuOpen(false);
                }}
              >
                Sign out
              </div>
            </div>
          )}
          <UpgradeDialog
            isOpen={planDialogOpen}
            isDarkMode={isDarkMode}
            onClose={() => setPlanDialogOpen(false)}
            onActivated={async (updatedAccount) => setAccount(updatedAccount)}
          />
        </div>
      ) : (
        <button
          type="button"
          style={signInButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            if (onSignInClick) onSignInClick();
          }}
        >
          Sign in / Sign up
        </button>
      )}
    </div>
  );
};

export default TopBar;
